import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import { post } from '../../Auths/AuthToken';
import {
  addDays,
  compareAsc,
  endOfDay,
  endOfMonth,
  endOfWeek,
  isBefore,
  isWithinInterval,
  parseISO,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { setAppPopupFacility } from '../../../redux/slices/scheduler/appointmentSlice';

function AppCalander({
  providerId = 0,
  appDate,
  callback = () => {},
  facilityList = [],
  facilityLoc,
  mode = 0,
  setMode = () => {},
}) {
  const [allData, setAllData] = useState([]);
  const [allBlockData, setAllBlockData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const APPOINTMENT_STATUS = {
    NO_SHOW: '1',
    CANCELED: '2',
    RESCHEDULED: '3',
  };

  const [facilitySetting, setFacilitySetting] = useState(null);
  const [calTime, setCalTime] = useState({
    startTime: null,
    endTime: null,
    scrliiTime: null,
  });

  const dispatch = useDispatch();
  const { user } = useSelector((store) => store.auth);
  const { defaultTimeFormat } = useSelector((store) => store.app);
  const { appPopUpFacility, patSrchDiasbled } = useSelector(
    (store) => store.appointment,
  );

  const [calPeriodFilter, setCalPeriodFilter] = useState({
    name: 'day',
    selectDate: appDate,
    startDate: appDate,
    endDate: appDate,
  });

  const parseTime = useCallback(
    (timeStr, refDate = calPeriodFilter.startDate) => {
      if (!timeStr || timeStr === 'NULL') return null;
      let parsed = null;

      // Handle full timestamp strings (e.g., "2026-04-12 06:00:00")
      // We extract strictly the TIME portion to ensure it remains relative to the day being viewed
      if (
        timeStr.includes(' ') &&
        timeStr.includes('-') &&
        timeStr.includes(':')
      ) {
        const timePart = timeStr.split(' ')[1];
        parsed = parse(timePart, 'HH:mm:ss', refDate);
      } else if (timeStr.includes(':')) {
        // Handle time strings with AM/PM or military formats
        if (timeStr.includes('AM') || timeStr.includes('PM')) {
          parsed = parse(timeStr, 'h:mm aa', refDate);
        } else {
          const parts = timeStr.split(':');
          if (parts.length === 3) {
            parsed = parse(timeStr, 'HH:mm:ss', refDate);
          } else if (parts.length === 2) {
            parsed = parse(timeStr, 'HH:mm', refDate);
          }
        }
      } else {
        parsed = new Date(timeStr);
      }

      const isValidDate = parsed && !isNaN(parsed.getTime());
      return isValidDate ? parsed : null;
    },
    [calPeriodFilter.startDate],
  );

  const weekStartsOnSetting = useMemo(() => {
    if (facilitySetting?.facility_start_day === '1') return 1;
    if (facilitySetting?.facility_start_day === '0') return 0;
    return 1; // Default to Monday as per project constants
  }, [facilitySetting]);

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () =>
      startOfWeek(new Date(), {
        weekStartsOn: weekStartsOnSetting,
      }),
    getDay,
    locales: {
      'en-US': require('date-fns/locale/en-US'),
    },
  });

  useEffect(() => {
    let activeRequest = true;

    const fetchData = async () => {
      if (!providerId) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await post('api/auth/appCalander', {
          resourceId: providerId,
          locationId: mode ? appPopUpFacility : facilityLoc,
          start: format(calPeriodFilter.startDate, 'yyyy-MM-dd'),
          end: format(calPeriodFilter.endDate, 'yyyy-MM-dd'),
          staffUserId: user?.id,
          mode: mode,
        });

        if (activeRequest) {
          if (response.status === 200) {
            setAllData(response.data);
            setAllBlockData(response?.data?.eventsClose || []);
            setFacilitySetting(response?.data?.facilitySetting || {});
            const overrides = Array.isArray(response?.data?.overrideSetting)
              ? response.data.overrideSetting
              : [response?.data?.overrideSetting].filter(Boolean);

            const activeOverride = overrides.find((o) => {
              const isWithinRange =
                o?.start_date &&
                o?.end_date &&
                startOfDay(calPeriodFilter.startDate) >=
                  startOfDay(new Date(o.start_date)) &&
                startOfDay(calPeriodFilter.startDate) <=
                  startOfDay(new Date(o.end_date));
              const dayId =
                calPeriodFilter.startDate.getDay() === 0
                  ? 7
                  : calPeriodFilter.startDate.getDay();
              return parseInt(o?.day_id) === dayId && isWithinRange;
            });

            const isOverride = activeOverride?.schedule_type === '1';

            const finalCalTime = {
              startTime: parseTime(
                response?.data?.facilitySetting?.facility_first_hour,
              ),
              endTime: parseTime(
                response?.data?.facilitySetting?.facility_last_hour,
              ),
              scrliiTime: parseTime(
                response?.data?.facilitySetting?.facility_scroll_hour,
              ),
            };
            setCalTime(finalCalTime);
          } else {
            setError('Failed to fetch calendar data.');
          }
        }
      } catch (err) {
        if (activeRequest) {
          console.error('Error fetching calendar data:', err);
          setError('An error occurred while loading the calendar.');
        }
      } finally {
        if (activeRequest) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      activeRequest = false;
    };
  }, [
    providerId,
    appPopUpFacility,
    calPeriodFilter,
    user,
    appDate,
    facilityLoc,
    mode,
  ]);

  const onRangeChange = useCallback(
    (range, view) => {
      if (view && calPeriodFilter?.name !== view) {
        if (range.length === 1 && view === 'day') {
          setCalPeriodFilter({
            ...calPeriodFilter,
            name: 'day',
            startDate: calPeriodFilter.selectDate,
            endDate: calPeriodFilter.selectDate,
          });
        } else if (range.length === 7) {
          setCalPeriodFilter({
            ...calPeriodFilter,
            name: 'week',
            startDate: startOfWeek(calPeriodFilter.selectDate, {
              weekStartsOn: weekStartsOnSetting,
            }),
            endDate: endOfWeek(calPeriodFilter.selectDate, {
              weekStartsOn: weekStartsOnSetting,
            }),
          });
        } else {
          setCalPeriodFilter({
            ...calPeriodFilter,
            name: 'month',
            startDate: startOfMonth(calPeriodFilter.selectDate),
            endDate: endOfMonth(calPeriodFilter.selectDate),
          });
        }
      }
    },
    [calPeriodFilter, facilitySetting?.facility_start_day],
  );

  const onNavigate = (newDate, view, action) => {
    if (view === 'day') {
      setCalPeriodFilter({
        ...calPeriodFilter,
        name: 'day',
        selectDate: newDate,
        startDate: newDate,
        endDate: newDate,
      });
      callback(new Date(newDate));
    } else if (view === 'week') {
      setCalPeriodFilter({
        ...calPeriodFilter,
        name: 'week',
        selectDate: newDate,
        startDate: startOfWeek(newDate, {
          weekStartsOn: weekStartsOnSetting,
        }),
        endDate: endOfWeek(newDate, {
          weekStartsOn: weekStartsOnSetting,
        }),
      });
    } else if (view === 'month') {
      setCalPeriodFilter({
        ...calPeriodFilter,
        name: 'month',
        selectDate: newDate,
        startDate: startOfMonth(newDate),
        endDate: endOfMonth(newDate),
      });
    }
  };

  const eventPropGetter = useCallback((event) => {
    let style = {
      color: 'black',
      backgroundColor: 'blue',
      pointerEvents: 'auto',
    };

    if (event.isVirtual) {
      if (event.type === 'break') {
        style = {
          backgroundColor: 'powderblue',
          color: 'black',
          pointerEvents: 'none',
          border: '1px solid #bdd1d1',
          fontSize: '11px',
          padding: '2px',
        };
      } else if (event.type === 'block') {
        style = {
          backgroundColor: '#7a7e7c',
          color: 'white',
          pointerEvents: 'none',
          fontSize: '11px',
          padding: '2px',
        };
      }
    } else {
      if (event.app_status === APPOINTMENT_STATUS.NO_SHOW) {
        style.backgroundColor = 'gray';
      } else if (event.app_status === APPOINTMENT_STATUS.CANCELED) {
        style.backgroundColor = 'gray';
      } else if (event.app_status === APPOINTMENT_STATUS.RESCHEDULED) {
        style.backgroundColor = 'gray';
      } else {
        if (event.app_color) {
          style.backgroundColor = event.app_color;
        }
      }
    }

    return { style };
  }, []);

  const handleSelectSlot = useCallback(
    (event) => {
      if (calPeriodFilter?.name === 'month' || event.start.getHours() >= 7) {
        callback(new Date(event?.start));
      }
    },
    [callback, calPeriodFilter],
  );

  const onSelectEvent = useCallback(
    (event) => {
      if (event.isVirtual) return; // Ignore clicks on breaks/blocks
      callback(new Date(event?.start));
    },
    [callback],
  );

  const getOverrideForDate = useCallback(
    (date) => {
      const dayId = date.getDay() === 0 ? 7 : date.getDay();
      const overrides = Array.isArray(allData?.overrideSetting)
        ? allData.overrideSetting
        : [allData?.overrideSetting].filter(Boolean);

      const checkDate = startOfDay(date).getTime();

      return overrides.find((o) => {
        if (!o?.start_date || !o?.end_date || o?.schedule_type !== '1')
          return false;

        // Use parseISO to handle strings like 'yyyy-MM-dd' correctly without TZ shifts
        const s = startOfDay(parseISO(o.start_date)).getTime();
        const e = startOfDay(parseISO(o.end_date)).getTime();

        const isWithinRange = checkDate >= s && checkDate <= e;
        return parseInt(o?.day_id) === dayId && isWithinRange;
      });
    },
    [allData?.overrideSetting],
  );

  const combinedEvents = useMemo(() => {
    if (calPeriodFilter.name === 'month') {
      return allData?.events || [];
    }

    const virtualEvents = [];
    const viewStart = startOfDay(calPeriodFilter.startDate);
    const viewEnd = endOfDay(calPeriodFilter.endDate);

    // 1. Generate Breaks from Overrides (Tab 3) or Regular Schedule (Tab 1)
    let cur = new Date(viewStart);
    while (cur <= viewEnd) {
      const override = getOverrideForDate(cur);
      const overrides = Array.isArray(allData?.overrideSetting)
        ? allData.overrideSetting
        : [allData?.overrideSetting].filter(Boolean);
      const isWithinAnyOverrideRange = overrides.some((o) => {
        return (
          o?.start_date &&
          o?.end_date &&
          startOfDay(cur) >= startOfDay(new Date(o.start_date)) &&
          startOfDay(cur) <= startOfDay(new Date(o.end_date))
        );
      });

      // Break logic: Tab 3 overrides Tab 1 if in range
      const isOverrideActive =
        isWithinAnyOverrideRange &&
        override?.schedule_type === '1' &&
        override?.is_closed === '1';

      const dayOfWeek = cur.getDay() === 0 ? 7 : cur.getDay();
      const regularDaySetting = allData?.facilityHourSetting?.find(
        (item) => parseInt(item.day_id) === dayOfWeek,
      );

      const dailyBreaks = [
        {
          start: isOverrideActive
            ? override?.break_first_start_time
            : allData?.facilitySetting?.firstBreakStart ||
              allData?.firstBreakStart ||
              regularDaySetting?.day_break_start_time,
          end: isOverrideActive
            ? override?.break_first_end_time
            : allData?.facilitySetting?.firstBreakEnd ||
              allData?.firstBreakEnd ||
              regularDaySetting?.day_break_end_time,
          title: isOverrideActive
            ? override?.break_first_name
            : allData?.facilitySetting?.firstBreakName ||
              allData?.firstBreakName ||
              regularDaySetting?.break_name ||
              'Break',
        },
        {
          start: isOverrideActive
            ? override?.break_second_start_time
            : allData?.facilitySetting?.secondBreakStart ||
              allData?.secondBreakStart,
          end: isOverrideActive
            ? override?.break_second_end_time
            : allData?.facilitySetting?.secondBreakEnd ||
              allData?.secondBreakEnd,
          title: isOverrideActive
            ? override?.break_second_name
            : allData?.facilitySetting?.secondBreakName ||
              allData?.secondBreakName ||
              'Break',
        },
      ];

      dailyBreaks.forEach((b) => {
        if (b.start && b.end) {
          const s = parseTime(b.start, cur);
          const e = parseTime(b.end, cur);
          if (s && e && compareAsc(s, e) < 0) {
            virtualEvents.push({
              start: s,
              end: e,
              title: b.title,
              isVirtual: true,
              type: 'break',
            });
          }
        }
      });
      cur = addDays(cur, 1);
    }

    // 2. Generate Blocks from Temporary Overrides (Tab 2)
    allBlockData?.forEach((item) => {
      if (item.is_task === '2' && item.start && item.end) {
        virtualEvents.push({
          start: parseISO(item.start),
          end: parseISO(item.end),
          title: item.title || 'Blocked',
          isVirtual: true,
          type: 'block',
        });
      }
    });

    return [...(allData?.events || []), ...virtualEvents];
  }, [
    allData,
    allBlockData,
    calPeriodFilter.startDate,
    calPeriodFilter.endDate,
    getOverrideForDate,
    parseTime,
  ]);

  const dayPropGetter = useCallback(
    (date) => {
      const dayOfWeek = date.getDay() === 0 ? 7 : date.getDay();
      const dateStr = format(date, 'yyyy-MM-dd');

      // 1. Tab 2 (Temporary Overrides) Priority
      // If there is ANY "Open my calendar" task for this day, we return {} to unblock the day.
      // Individual slots within the day will still be grayed out in slotPropGetter if they aren't part of the task.
      const hasOpenInterval =
        allData?.openMyCalander?.some((interval) => {
          const start = parseISO(interval.start_time);
          return (
            format(start, 'yyyy-MM-dd') === dateStr &&
            compareAsc(
              parseISO(interval.start_time),
              parseISO(interval.end_time),
            ) < 0
          );
        }) || false;

      if (hasOpenInterval) {
        return {};
      }

      // 2. View-specific logic (Tab 3 Override vs Tab 1 Regular)
      if (calPeriodFilter.name !== 'month') {
        // --- Day/Week View: Use Tab 3 Overrides ---
        const overrides = Array.isArray(allData?.overrideSetting)
          ? allData.overrideSetting
          : [allData?.overrideSetting].filter(Boolean);

        const isWithinAnyOverrideRange = overrides.some((o) => {
          if (!o?.start_date || !o?.end_date || o?.schedule_type !== '1')
            return false;
          const d = startOfDay(date).getTime();
          const s = startOfDay(parseISO(o.start_date)).getTime();
          const e = startOfDay(parseISO(o.end_date)).getTime();
          return d >= s && d <= e;
        });

        if (isWithinAnyOverrideRange) {
          const override = getOverrideForDate(date);
          if (override && override.schedule_type === '1') {
            if (override.is_closed === '0') {
              return {
                className: 'blocked-day',
                style: {
                  pointerEvents: 'none',
                  backgroundColor: '#7a7e7c',
                },
              };
            }
            return {}; // Day has a specific entry and is open
          } else {
            // Within range but no entry for this specific day_id -> Gray out
            return {
              className: 'blocked-day',
              style: {
                pointerEvents: 'none',
                backgroundColor: '#7a7e7c',
              },
            };
          }
        }
      } else {
        // --- Month View: Follow Tab 1 Regular Setting as requested ---
        const regularDaySetting = allData?.facilityHourSetting?.find(
          (item) => parseInt(item.day_id) === dayOfWeek,
        );
        if (regularDaySetting && regularDaySetting.is_closed === '0') {
          return {
            className: 'blocked-day',
            style: {
              pointerEvents: 'none',
              backgroundColor: '#7a7e7c',
            },
          };
        }
      }

      // 3. Tab 1 Supplemental Logic (End Date & Specific Blocks)
      const providerScheduleEnd =
        allData?.providerScheduleEnd &&
        new Date(allData?.providerScheduleEnd?.end_date);
      if (
        providerScheduleEnd &&
        startOfDay(date) > startOfDay(providerScheduleEnd)
      ) {
        return {
          className: 'blocked-day',
          style: {
            pointerEvents: 'none',
            backgroundColor: '#7a7e7c',
          },
        };
      }

      // Check for specific manual blocks in allBlockData that match this date
      const hasSpecificBlock = allBlockData?.some((item) => {
        const isSameDay = parseInt(item.day_id) === parseInt(dayOfWeek);
        if (!isSameDay) return false;

        // Date-specific block match
        if (
          item.start &&
          format(parseISO(item.start), 'yyyy-MM-dd') === dateStr
        )
          return true;

        // Global day-of-week block match
        const isGlobalBlock = !item.start || item.title === 'Full day Block';
        if (isGlobalBlock) {
          if (dayOfWeek === 6 || dayOfWeek === 7) return true; // Always block weekends if off
          if (calPeriodFilter.name !== 'month') return true; // Block weekdays globally in Day/Week view
        }

        return false;
      });

      if (hasSpecificBlock) {
        return {
          className: 'blocked-day',
          style: {
            pointerEvents: 'none',
            backgroundColor: '#7a7e7c',
          },
        };
      }

      return {};
    },
    [
      allData?.openMyCalander,
      allData?.overrideSetting,
      allData?.providerScheduleEnd,
      allData?.facilityHourSetting,
      allBlockData,
      getOverrideForDate,
      calPeriodFilter.name,
    ],
  );

  const { formats } = useMemo(() => {
    return {
      formats: {
        eventTimeRangeFormat: ({ start, end }, culture, localizer) => {},
      },
    };
  }, []);

  const slotPropGetter = useCallback(
    (date) => {
      // Only apply slot-level logic in 'day' view as requested
      if (calPeriodFilter.name === 'month') return {};

      // 1. Tab 2 (Temporary Overrides) - Highest Priority
      const isOpenTaskInterval =
        allData?.openMyCalander?.some((interval) => {
          const start = parseISO(interval.start_time);
          const end = parseISO(interval.end_time);
          return (
            compareAsc(start, end) < 0 && isWithinInterval(date, { start, end })
          );
        }) || false;

      if (isOpenTaskInterval) {
        return { style: { backgroundColor: '#fff', color: 'black' } };
      }

      const isBlockedTaskInterval =
        allBlockData &&
        allBlockData.some((item) => {
          if (item.is_task === '2' && item.start && item.end) {
            const start = parseISO(item.start);
            const end = parseISO(item.end);
            return isWithinInterval(date, { start, end });
          }
          return false;
        });

      if (isBlockedTaskInterval) {
        return { style: { backgroundColor: '#7a7e7c', color: 'white' } };
      }

      // 2. Identify Active Working Hours (Tab 3 or Tab 1)
      const override = getOverrideForDate(date);
      const overrides = Array.isArray(allData?.overrideSetting)
        ? allData.overrideSetting
        : [allData?.overrideSetting].filter(Boolean);

      const isWithinAnyOverrideRange = overrides.some((o) => {
        if (!o?.start_date || !o?.end_date || o?.schedule_type !== '1')
          return false;
        const d = startOfDay(date).getTime();
        const s = startOfDay(parseISO(o.start_date)).getTime();
        const e = startOfDay(parseISO(o.end_date)).getTime();
        return d >= s && d <= e;
      });

      const dayOfWeek = date.getDay() === 0 ? 7 : date.getDay();
      const regularDaySetting = allData?.facilityHourSetting?.find(
        (item) => parseInt(item.day_id) === dayOfWeek,
      );

      let startHour = null;
      let lastHour = null;

      if (isWithinAnyOverrideRange) {
        // Tab 3 logic: Override Tab 1
        if (override) {
          startHour = parseTime(override.open_time, date);
          lastHour = parseTime(override.close_time, date);
        } else {
          // Inside override range but no specific day entry or closed manually
          return { style: { backgroundColor: 'gray', color: 'white' } };
        }
      } else {
        // Tab 1 logic: Regular Schedule
        const providerScheduleEnd =
          allData?.providerScheduleEnd &&
          new Date(allData?.providerScheduleEnd?.end_date);

        const isRegularBlocked =
          allBlockData &&
          allBlockData.some((item) => {
            return (
              parseInt(item.day_id) === parseInt(dayOfWeek) &&
              (!item.start || item.title === 'Full day Block')
            );
          });

        if (
          (providerScheduleEnd &&
            startOfDay(date) > startOfDay(providerScheduleEnd)) ||
          isRegularBlocked
        ) {
          return { style: { backgroundColor: 'gray', color: 'white' } };
        }

        startHour = parseTime(
          allData?.providerScheduleEnd?.open_time ||
            allData?.startHour ||
            allData?.facilitySetting?.facility_first_hour,
          date,
        );
        lastHour = parseTime(
          allData?.providerScheduleEnd?.close_time ||
            allData?.lastHour ||
            allData?.facilitySetting?.facility_last_hour,
          date,
        );
      }

      // Gray out if outside working hours
      const isOutsideWorkingHours =
        (startHour &&
          (date.getHours() < startHour.getHours() ||
            (date.getHours() === startHour.getHours() &&
              date.getMinutes() < startHour.getMinutes()))) ||
        (lastHour &&
          (date.getHours() > lastHour.getHours() ||
            (date.getHours() === lastHour.getHours() &&
              date.getMinutes() >= lastHour.getMinutes())));

      if (isOutsideWorkingHours) {
        return { style: { backgroundColor: 'gray', color: 'white' } };
      }

      return {};
    },
    [
      allData?.openMyCalander,
      allData?.overrideSetting,
      allData?.providerScheduleEnd,
      allData?.facilityHourSetting,
      allData?.startHour,
      allData?.lastHour,
      allData?.facilitySetting,
      allBlockData,
      getOverrideForDate,
      parseTime,
      calPeriodFilter.name,
    ],
  );

  const handleCheckboxChange = (event) => {
    //const { name, value, checked } = event.target;
    dispatch(setAppPopupFacility(parseInt(event.target.value)));

    setMode(1);
  };

  const CustomToolbar = (toolbar) => {
    return (
      <div
        className="rbc-toolbar"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          fontSize: '14px',
        }}
      >
        <div style={{ display: 'flex', width: '100%', margin: '10px' }}>
          {(calPeriodFilter.name === 'day' && allData?.displayDate && (
            <span className="rbc-toolbar-labels">{`${allData?.dayName}, ${allData?.displayDate}:  ${
              allData?.providerOfficeTime?.toLowerCase()?.trim() === 'closed'
                ? getOverrideForDate(calPeriodFilter.selectDate) ||
                  allData?.openMyCalander?.some((interval) => {
                    const start = parseISO(interval.start_time);
                    return (
                      format(start, 'yyyy-MM-dd') ===
                        format(calPeriodFilter.selectDate, 'yyyy-MM-dd') &&
                      compareAsc(
                        parseISO(interval.start_time),
                        parseISO(interval.end_time),
                      ) < 0
                    );
                  })
                  ? '00:00'
                  : 'Closed'
                : allData?.providerOfficeTime
            }`}</span>
          )) ||
            (calPeriodFilter.name === 'week' && (
              <span className="rbc-toolbar-labels">{`Week: ${format(
                calPeriodFilter?.startDate,
                'MMMM d, yyyy',
              )} to ${format(calPeriodFilter?.endDate, 'MMMM d, yyyy')}`}</span>
            )) ||
            (calPeriodFilter.name === 'month' && (
              <span className="rbc-toolbar-labels">{`Month: ${format(
                calPeriodFilter?.endDate,
                'MMMM, yyyy',
              )}`}</span>
            ))}
        </div>
        <div
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <span className="rbc-btn-group">
            <button
              type="button"
              onClick={
                isBefore(
                  new Date(appDate).setHours(0, 0, 0, 0),
                  new Date().setHours(0, 0, 0, 0),
                )
                  ? () => {}
                  : () => toolbar.onNavigate('PREV')
              }
            >
              Prev
            </button>
            <button
              type="button"
              onClick={
                isBefore(
                  new Date(appDate).setHours(0, 0, 0, 0),
                  new Date().setHours(0, 0, 0, 0),
                )
                  ? () => {}
                  : () => toolbar.onNavigate('TODAY')
              }
            >
              Today
            </button>
            <button
              type="button"
              onClick={
                isBefore(
                  new Date(appDate).setHours(0, 0, 0, 0),
                  new Date().setHours(0, 0, 0, 0),
                )
                  ? () => {}
                  : () => toolbar.onNavigate('NEXT')
              }
            >
              Next
            </button>
          </span>
          {/* {() => {
          if (calPeriodFilter.name === 'day') {
            return <span className="rbc-toolbar-label">{toolbar.label}</span>;
          }
        }} */}
          {/* <span className="rbc-toolbar-label">{toolbar.label}</span> */}
          <span className="rbc-btn-group">
            <button
              className={calPeriodFilter.name === 'day' ? 'rbc-active' : ''}
              type="button"
              onClick={
                isBefore(
                  new Date(appDate).setHours(0, 0, 0, 0),
                  new Date().setHours(0, 0, 0, 0),
                )
                  ? () => {}
                  : () => toolbar.onView('day')
              }
            >
              Day
            </button>
            <button
              className={calPeriodFilter.name === 'week' ? 'rbc-active' : ''}
              type="button"
              onClick={
                isBefore(
                  new Date(appDate).setHours(0, 0, 0, 0),
                  new Date().setHours(0, 0, 0, 0),
                )
                  ? () => {}
                  : () => toolbar.onView('week')
              }
            >
              Week
            </button>
            <button
              className={calPeriodFilter.name === 'month' ? 'rbc-active' : ''}
              type="button"
              onClick={
                isBefore(
                  new Date(appDate).setHours(0, 0, 0, 0),
                  new Date().setHours(0, 0, 0, 0),
                )
                  ? () => {}
                  : () => toolbar.onView('month')
              }
            >
              Month
            </button>
          </span>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className=" width100 apt-sldr-loc-sec">
        {error && (
          <div
            style={{
              padding: '10px',
              margin: '10px',
              backgroundColor: '#fee2e2',
              color: '#b91c1c',
              borderRadius: '4px',
              fontSize: '14px',
              textAlign: 'center',
            }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex' }}>
          <div className="sldr-loctn-slectn" style={{ width: '30%' }}>
            <span className="">
              <input
                type="radio"
                name={'appPopUpFacility'}
                checked={
                  appPopUpFacility === parseInt(allData?.defaultLocation)
                }
                onChange={handleCheckboxChange}
                value={
                  allData?.defaultLocation
                    ? parseInt(allData.defaultLocation)
                    : 0
                }
              />
            </span>
            {!isLoading ? (
              <span className="" style={{ padding: '0px 10px' }}>
                {allData?.defaultLocationName}
                {`${allData?.is_telemedicine === '1' ? '(Remote)' : ''}`}
              </span>
            ) : (
              <span className="" style={{ padding: '0px 10px' }}>
                Loading...
              </span>
            )}
          </div>
          <div className="sldr-loctn-slectn" style={{ width: '30%' }}>
            <span className="">
              <input
                type="radio"
                name={'appPopUpFacility'}
                checked={appPopUpFacility === 0}
                onChange={handleCheckboxChange}
                value={0}
              />
            </span>
            <span className="" style={{ padding: '0px 10px' }}>
              All Locations
              {`${allData?.is_telemedicine === '1' ? '(Remote)' : ''}`}
            </span>
          </div>
        </div>
        <div className="left med-label width100 fontsize14">
          Total appointments: {allData?.appointmentTotalCount}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;New patients:
          {allData?.appointmentTotalCountNewPt}
        </div>
      </div>

      <Calendar
        key={allData?.events?.length + combinedEvents.length}
        localizer={localizer}
        {...(defaultTimeFormat?.withoutSecond === 'HH:mm'
          ? {
              formats: {
                timeGutterFormat: 'HH:mm', // 24hr time format
                eventTimeRangeFormat: ({ start, end }) =>
                  `${start.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false, // 24hr format
                  })} - ${end.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false, // 24hr format
                  })}`,
              },
            }
          : formats)}
        events={combinedEvents}
        startAccessor={(event) => {
          return new Date(event.start);
        }}
        endAccessor={(event) => {
          return new Date(event.end);
        }}
        titleAccessor={(event) => {
          let title = event.title;
          if (event.app_status === APPOINTMENT_STATUS.NO_SHOW) {
            title = 'No Show';
          } else if (event.app_status === APPOINTMENT_STATUS.CANCELED) {
            title = 'Canceled';
          } else if (event.app_status === APPOINTMENT_STATUS.RESCHEDULED) {
            title = 'Rescheduled';
          }
          return title;
        }}
        toolbar={true}
        selectable={true}
        views={['month', 'week', 'day']}
        defaultView={calPeriodFilter.name || 'day'}
        date={calPeriodFilter?.selectDate}
        //defaultDate={calPeriodFilter?.selectDate || new Date()}
        eventPropGetter={eventPropGetter}
        dayPropGetter={dayPropGetter}
        slotPropGetter={slotPropGetter}
        style={{ height: 500, margin: '10px 10px', width: '570px' }}
        min={calTime?.startTime ? new Date(calTime?.startTime) : undefined} // Set the minimum time
        max={calTime?.endTime ? new Date(calTime?.endTime) : undefined} // Set the maximum time
        step={facilitySetting?.calander_default_slot || 15}
        // formats={formats}
        onRangeChange={onRangeChange}
        onNavigate={onNavigate}
        onSelectSlot={handleSelectSlot}
        onSelectEvent={onSelectEvent}
        scrollToTime={
          calTime?.scrliiTime ? new Date(calTime?.scrliiTime) : new Date()
        }
        components={{
          toolbar: CustomToolbar,
        }}
      />
    </div>
  );
}

export default AppCalander;
